// React import
import React, { useState, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import { formatDisplayDate } from '../../utils/date.js';
import dayjs from 'dayjs';

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha, useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { faCircle as faCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import { faPlane, faShip, faTruck, faMagnifyingGlass, faCalendarDays } from '@fortawesome/pro-light-svg-icons';

// Local import
import Where from '../../Where.js';
import PIPELINE from './AccountFunnelPipeline.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';

function Filters(props) {
    const { filter, setFilter, accounts, seaPorts, moreFilters, forceSearchOptions, mode, featureToggle = {}, org, me, includeNew, setIncludeNew } = props;
    const [ advanced, setAdvanced ] = useState(false);
    const theme = useTheme();

    const pillSx = { textTransform: 'none', fontSize: '11px', borderRadius: '6px', padding: '3px 10px', minWidth: 0, lineHeight: 1.4 };
    const pillActive = { ...pillSx, backgroundColor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 600, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) } };
    const pillInactive = { ...pillSx, color: theme.palette.text.secondary, '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.04) } };

    const STATUS_PILLS = [
        { label: 'Draft', filter: { oppStatus: 'draft' } },
        { label: 'Sent', filter: { oppStatus: 'sent' }, color: '#3b82f6' },
        { label: 'Won', filter: { oppStatus: 'won' }, color: '#16a34a' },
        { label: 'Lost', filter: { oppStatus: 'lost' }, color: '#dc2626' },
    ];

    const searchOptions = useMemo(() => {
        if (forceSearchOptions) return forceSearchOptions;
        const tags = _(accounts)
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        const updateTags = _(accounts)
            .map(a => a.updates)
            .compact()
            .flatten()
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .value();
        const owners = _(accounts)
            .map(a => a.owner)
            .sort()
            .uniq()
            .compact()
            .value();
        const names = _(accounts)
            .map(a => a.name)
            .sort()
            .value();
        return _.compact([
            ...tags.map(t => ({ type: 'tag', value: t })),
            ...updateTags.map(t => ({ type: 'tag', value: t })),
            ...[mode === 'accounts' ? { type: 'tag', value: { label: 'Supplier' } } : null],
            ...names.map(n => ({ type: 'name', value: n })),
            ...owners.map(n => ({ type: 'owner', value: n })),
        ]);
    }, [accounts, forceSearchOptions, mode]);

    const Pop = props => {
        const { className, anchorEl, style, ...rest } = props
        const bound = anchorEl.getBoundingClientRect()
        return <div {...rest} style={{
            position: 'absolute',
            zIndex: 9999,
            width: bound.width
        }} />
    }

    const transportBtn = (method, icon) => {
        const active = filter.transportationMethods.includes(method);
        return (
            <IconButton
                size='small'
                onClick={() => setFilter(f => ({
                    ...f,
                    transportationMethods: active ? f.transportationMethods.filter(t => t !== method) : [...f.transportationMethods, method]
                }))}
                sx={{
                    fontSize: '13px', borderRadius: '6px', padding: '4px 8px',
                    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                    backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': { backgroundColor: active ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.text.primary, 0.04) },
                }}
            >
                <FontAwesomeIcon icon={icon} />
            </IconButton>
        );
    };

    // Date range for display
    const dateRange = (() => {
        const p = filter.createdPeriod;
        if (!p || p === 'all') return null;
        const from = p === 'week' ? dayjs().startOf('isoWeek') : p === 'month' ? dayjs().startOf('month') : p === 'quarter' ? dayjs().startOf('quarter')
            : p === 'past-week' ? dayjs().subtract(1, 'week').startOf('isoWeek') : p === 'past-month' ? dayjs().subtract(1, 'month').startOf('month')
            : p === 'past-quarter' ? dayjs().subtract(1, 'quarter').startOf('quarter') : null;
        const to = p === 'past-week' ? dayjs().subtract(1, 'week').endOf('isoWeek') : p === 'past-month' ? dayjs().subtract(1, 'month').endOf('month')
            : p === 'past-quarter' ? dayjs().subtract(1, 'quarter').endOf('quarter') : dayjs();
        return from ? { from, to } : null;
    })();

    return (
        <Box sx={{ backgroundColor: '#F9FAFB', border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, borderRadius: '12px', p: '12px 16px', mb: 2 }}>
            {/* ─── Row 1: Owner + Date + Phase pills ─── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {org && me && (
                    <OpportunitiesFilterOwner
                        org={org}
                        filter={{ members: filter.createdBy ? filter.createdBy.split(',') : [] }}
                        setFilter={({ members }) => setFilter(f => ({ ...f, createdBy: members?.length > 0 ? members.join(',') : null }))}
                        me={me}
                        noMarginRight={true}
                        noSelectionText='All Owners'
                        mineText='My accounts'
                    />
                )}

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                <Tooltip title={`Filter accounts by activity: timeline updates${org?.integrations?.cargowise?.enabled ? ' (including CW syncs)' : ''}, tasks, and opportunities created in the selected period`} placement='bottom' arrow>
                <Select
                    size='small'
                    value={(() => {
                        if (filter.inactiveDays) return `inactive:${filter.inactiveDays}`;
                        if (filter.createdPeriod) return `period:${filter.createdPeriod}`;
                        return 'none';
                    })()}
                    onChange={e => {
                        const val = e.target.value;
                        if (val === 'none') {
                            setFilter(f => ({ ...f, createdPeriod: null, inactiveDays: null }));
                        } else if (val.startsWith('inactive:')) {
                            setFilter(f => ({ ...f, createdPeriod: null, inactiveDays: parseInt(val.replace('inactive:', '')) }));
                        } else if (val.startsWith('period:')) {
                            setFilter(f => ({ ...f, createdPeriod: val.replace('period:', ''), inactiveDays: null }));
                        }
                    }}
                    renderValue={(val) => {
                        const labels = {
                            'period:week': 'Active: This Week', 'period:month': 'Active: This Month', 'period:quarter': 'Active: This Quarter',
                            'period:past-week': 'Active: Last Week', 'period:past-month': 'Active: Last Month', 'period:past-quarter': 'Active: Last Quarter',
                            'inactive:7': 'Inactive: 7+ days', 'inactive:30': 'Inactive: 30+ days', 'inactive:60': 'Inactive: 60+ days', 'inactive:90': 'Inactive: 90+ days',
                            'none': 'Show All',
                        };
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: '12px', opacity: 0.5 }} />
                                <span>{labels[val] || 'Show All'}</span>
                                {dateRange && <Typography component='span' sx={{ fontSize: '10px', opacity: 0.6, ml: '2px' }}>({dateRange.from.format('MMM D')} – {dateRange.to.format('MMM D')})</Typography>}
                            </Box>
                        );
                    }}
                    sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', '& .MuiSelect-select': { py: '5px', pr: '28px !important' }, minWidth: 0, ...((filter.createdPeriod || filter.inactiveDays) ? { color: '#1976d2', fontWeight: 700 } : {}) }}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value='none'>Show All</MenuItem>
                    <MenuItem disabled sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active in</MenuItem>
                    <MenuItem value='period:week'>This Week</MenuItem>
                    <MenuItem value='period:month'>This Month</MenuItem>
                    <MenuItem value='period:quarter'>This Quarter</MenuItem>
                    <MenuItem value='period:past-week'>Last Week</MenuItem>
                    <MenuItem value='period:past-month'>Last Month</MenuItem>
                    <MenuItem value='period:past-quarter'>Last Quarter</MenuItem>
                    <MenuItem disabled sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inactive for</MenuItem>
                    <MenuItem value='inactive:7'>7+ days</MenuItem>
                    <MenuItem value='inactive:30'>30+ days</MenuItem>
                    <MenuItem value='inactive:60'>60+ days</MenuItem>
                    <MenuItem value='inactive:90'>90+ days</MenuItem>
                </Select>
                </Tooltip>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                {/* Phase pills */}
                <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                    {PIPELINE.map(pipeline => {
                        const active = filter.tags.some(t => t.label === `Pipeline: ${pipeline}`);
                        return (
                            <Button key={pipeline} size='small' onClick={() => {
                                if (active) setFilter(f => ({ ...f, tags: f.tags.filter(t => t.label !== `Pipeline: ${pipeline}`) }));
                                else setFilter(f => ({ ...f, tags: [...f.tags, { label: `Pipeline: ${pipeline}` }] }));
                            }} sx={active ? pillActive : pillInactive} startIcon={active ? <FontAwesomeIcon icon={faCircleSolid} style={{ fontSize: '6px' }} /> : null}>{pipeline}</Button>
                        );
                    })}
                </Box>

                <Box sx={{ flex: 1 }} />
                {mode === 'accounts' && setIncludeNew && featureToggle['feature.accounts-hide-new'] && (
                    <Tooltip title="Include accounts in the 'New' pipeline stage (off by default; the 'New' kanban column is hidden)" arrow placement='bottom'>
                        <FormControlLabel control={<Switch size='small' checked={!!includeNew} onChange={() => setIncludeNew(!includeNew)} />} label={<Typography variant='caption' sx={{ fontSize: '11px' }}>Including 'New'</Typography>} />
                    </Tooltip>
                )}
                <Tooltip title='Include accounts marked as inactive or archived' arrow placement='bottom'>
                    <FormControlLabel control={<Switch size='small' checked={!!filter.incInactive} onChange={() => setFilter(f => ({ ...f, incInactive: !f.incInactive }))} />} label={<Typography variant='caption' sx={{ fontSize: '11px' }}>Show Deactivated</Typography>} />
                </Tooltip>
                {_.some(accounts, a => !a.address || !a.address.countryCode || a.address.countryCode === 'NA') && (
                    <Tooltip title='Show only accounts with missing or invalid address information' arrow placement='bottom'>
                        <FormControlLabel control={<Switch size='small' checked={!!filter.onlyAddressErrors} onChange={() => setFilter(f => ({ ...f, onlyAddressErrors: !f.onlyAddressErrors }))} />} label={<Typography variant='caption' sx={{ fontSize: '11px' }}>Addr. errors</Typography>} />
                    </Tooltip>
                )}
            </Box>

            {/* ─── Row 2: Opportunities status pills + type + Transport ─── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '8px', flexWrap: 'wrap' }}>
                <Tooltip title='Filter accounts by the status of their opportunities' arrow placement='bottom'>
                    <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 600 }}>Opportunities:</Typography>
                </Tooltip>
                <Box sx={{ display: 'flex', gap: '3px' }}>
                    <Button size='small' onClick={() => setFilter(f => ({ ...f, oppStatus: null }))} sx={!filter.oppStatus ? pillActive : pillInactive}>All</Button>
                    {STATUS_PILLS.map(pill => {
                        const active = filter.oppStatus === pill.filter.oppStatus;
                        const color = pill.color || theme.palette.primary.main;
                        return (
                            <Button key={pill.label} size='small' onClick={() => setFilter(f => ({ ...f, oppStatus: active ? null : pill.filter.oppStatus }))}
                                sx={active ? { ...pillSx, backgroundColor: alpha(color, 0.1), color, fontWeight: 600, '&:hover': { backgroundColor: alpha(color, 0.18) } } : pillInactive}
                                startIcon={active ? <FontAwesomeIcon icon={faCircleSolid} style={{ fontSize: '6px', color }} /> : null}
                            >{pill.label}</Button>
                        );
                    })}
                </Box>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                <Select
                    size='small'
                    value={filter.oppType || 'all'}
                    onChange={e => setFilter(f => ({ ...f, oppType: e.target.value === 'all' ? null : e.target.value }))}
                    sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 100, '& .MuiSelect-select': { py: '4px' } }}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value='all'>All Types</MenuItem>
                    <MenuItem value='one-off'>Quotes</MenuItem>
                    <MenuItem value='contract'>Contracts</MenuItem>
                </Select>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                <Tooltip title='Filter accounts by transport mode used in their opportunities' arrow placement='bottom'>
                    <Box sx={{ display: 'flex', gap: '4px' }}>
                        {transportBtn('sea', faShip)}
                        {transportBtn('air', faPlane)}
                        {transportBtn('land', faTruck)}
                    </Box>
                </Tooltip>
            </Box>

            {/* ─── Row 3: Popular Tags ─── */}
            {(() => {
                const tagCounts = {};
                const tagCanonical = {}; // stores the most common casing
                (accounts || []).forEach(a => (a.updates || []).forEach(u => (u.tags || []).forEach(t => {
                    if (!t.label) return;
                    const key = t.label.toLowerCase();
                    tagCounts[key] = (tagCounts[key] || 0) + 1;
                    // Keep the first seen casing as canonical
                    if (!tagCanonical[key]) tagCanonical[key] = t.label;
                })));
                const popularTags = _.sortBy(Object.entries(tagCounts), ([, count]) => -count).slice(0, 8).map(([key]) => tagCanonical[key]);
                if (popularTags.length === 0) return null;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '8px', flexWrap: 'wrap' }}>
                        <Tooltip title='Most used tags from timeline updates — click to filter accounts by activity tag' arrow placement='bottom'>
                            <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 600 }}>Activity:</Typography>
                        </Tooltip>
                        <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            {popularTags.map(label => {
                                const active = filter.tags.some(t => t.label?.toLowerCase() === label.toLowerCase());
                                return (
                                    <Button key={label} size='small' onClick={() => {
                                        if (active) setFilter(f => ({ ...f, tags: f.tags.filter(t => t.label?.toLowerCase() !== label.toLowerCase()) }));
                                        else setFilter(f => ({ ...f, tags: [...f.tags, { label }] }));
                                    }} sx={active ? pillActive : pillInactive}
                                    startIcon={active ? <FontAwesomeIcon icon={faCircleSolid} style={{ fontSize: '6px' }} /> : null}
                                    >{label}</Button>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })()}

            {/* ─── Row 4: Search ─── */}
            <Box sx={{ mt: '8px' }}>
                <Autocomplete
                    size='small'
                    slotProps={{ popper: { style: { zIndex: 9999 } } }}
                    value={[
                        ...filter.tags.filter(t => !t.label?.startsWith('Pipeline:')).map(t => ({ type: 'tag', value: t })),
                        ...filter.names.map(t => ({ type: 'name', value: t })),
                        ...filter.owners.map(t => ({ type: 'owner', value: t })),
                        ...filter.freetext.map(t => ({ type: 'freetext', value: t })),
                    ]}
                    options={searchOptions}
                    filterOptions={(options, state) => {
                        const { inputValue } = state;
                        const type = inputValue.startsWith('#') ? ['tag'] : inputValue.startsWith('@') ? ['name', 'owner'] : null;
                        if (!type) return [];
                        const value = inputValue.substr(1);
                        if (value.length === 0) return [];
                        return _.uniqWith(options.filter(o => type.includes(o.type) && (o.value.label || o.value).toLowerCase().startsWith(value.toLowerCase())), _.isEqual);
                    }}
                    getOptionLabel={option => {
                        if (typeof option === 'string') return option;
                        if (typeof option.value === 'string') return option.value;
                        if (typeof option.value?.label === 'string') return option.value.label;
                        return '';
                    }}
                    renderOption={(props, option) => {
                        const label = typeof option.value === 'string' ? option.value : option.value?.label || '';
                        const typeLabel = option.type === 'tag' ? '#' : option.type === 'name' ? '@' : option.type === 'owner' ? '👤' : '';
                        const typeColor = option.type === 'tag' ? '#8b5cf6' : option.type === 'name' ? '#3b82f6' : option.type === 'owner' ? '#059669' : '#64748b';
                        return (
                            <li {...props}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: typeColor, minWidth: '16px' }}>{typeLabel}</Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{label}</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', ml: 'auto' }}>
                                        {option.type === 'tag' ? 'Tag' : option.type === 'name' ? 'Account' : option.type === 'owner' ? 'Owner' : ''}
                                    </Typography>
                                </Box>
                            </li>
                        );
                    }}
                    freeSolo
                    multiple
                    onChange={(e, value) => {
                        const tags = [
                            ...filter.tags.filter(t => t.label?.startsWith('Pipeline:')),
                            ...value.filter(v => v.type === 'tag').map(t => t.value),
                            ...value.filter(v => typeof(v) === 'string' && v.startsWith('#')).map(t => ({ label: t.substr(1) }))
                        ];
                        const names = [
                            ...value.filter(v => v.type === 'name').map(t => t.value),
                            ...value.filter(v => typeof(v) === 'string' && v.startsWith('@')).map(t => t.substr(1))
                        ];
                        const owners = value.filter(v => v.type === 'owner').map(t => t.value);
                        const freetext = [
                            ...value.filter(v => v.type === 'freetext').map(t => t.value),
                            ...value.filter(v => typeof (v) === 'string' && !v.startsWith('#') && !v.startsWith('@')).map(t => t)
                        ];
                        setFilter(f => ({ ...f, tags, names, owners, freetext }));
                    }}
                    renderTags={(value, props) => (_.compact([
                        ...filter.tags.filter(t => !t.label?.startsWith('Pipeline:')).map((t, i) => <Chip size='small' label={t.label} {...props({ index: i })} />),
                        ...filter.names.map((n, i) => <Chip size='small' label={n} {...props({ index: filter.tags.filter(t => !t.label?.startsWith('Pipeline:')).length + i })} />),
                        ...filter.owners.map((n, i) => <Chip size='small' label={n} {...props({ index: filter.tags.filter(t => !t.label?.startsWith('Pipeline:')).length + filter.names.length + i })} />),
                        ...filter.freetext.map((n, i) => <Chip size='small' label={n} {...props({ index: filter.tags.filter(t => !t.label?.startsWith('Pipeline:')).length + filter.names.length + filter.owners.length + i })} />),
                    ]))}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder='Search... (@ for name, # for tag)'
                            size='small'
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '12px', opacity: 0.4 }} />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                        />
                    )}
                />
            </Box>

            {/* Date range display */}
            {dateRange && (
                <Typography variant='caption' sx={{ display: 'block', mt: '6px', ml: '2px', color: 'text.secondary', fontSize: '11px' }}>
                    Showing data from <b>{dateRange.from.format('ddd, MMM D')}</b> to <b>{dateRange.to.format('ddd, MMM D')}</b>
                </Typography>
            )}
            {moreFilters && (
                <Box sx={{ mt: 1 }}>
                    {moreFilters}
                </Box>
            )}

            {advanced && (
                <Box sx={{ border: '1px dashed #aaa', padding: '24px', mt: 1, borderRadius: '8px' }}>
                    <Typography variant='caption' component={Box} sx={{ pb: 1 }}>Advanced filters: </Typography>
                    <AdvancedFilters
                        accounts={accounts}
                        sources={filter.advanced.sources}
                        destinations={filter.advanced.destinations}
                        setSources={sources => setFilter(f => ({ ...f, advanced: { ...f.advanced, sources }}))}
                        setDestinations={destinations => setFilter(f => ({ ...f, advanced: { ...f.advanced, destinations }}))}
                        potentialProfit={filter.advanced.potentialProfit}
                        setPotentialProfit={potentialProfit => setFilter(f => ({ ...f, advanced: { ...f.advanced, potentialProfit }}))}
                        actualProfit={filter.advanced.actualProfit}
                        setActualProfit={actualProfit => setFilter(f => ({ ...f, advanced: { ...f.advanced, actualProfit }}))}
                        dateCreated={filter.advanced.dateCreated}
                        setDateCreated={dateCreated => setFilter(f => ({ ...f, advanced: { ...f.advanced, dateCreated }}))}
                        lastActivity={filter.advanced.lastActivity}
                        setLastActivity={lastActivity => setFilter(f => ({ ...f, advanced: { ...f.advanced, lastActivity }}))}
                        seaPorts={seaPorts}
                    />
                </Box>
            )}
        </Box>
    );
}

function AdvancedFilters(props) {
    const { sources, destinations, setSources, setDestinations, seaPorts, 
        potentialProfit, setPotentialProfit, actualProfit, setActualProfit, 
        dateCreated, setDateCreated, lastActivity, setLastActivity, accounts, opportunities } = props;
    const [ errors, setErrors ] = useState([]);
    const [ incEverywhere, setIncEverywhere ] = useState(false);

    const [ minPP = 0, maxPP = 1000000] = potentialProfit;
    const [ minAP = 0, maxAP = 1000000] = actualProfit;

    const [ minCreated, maxCreated ] = useMemo(() => {
        if (!accounts) return [0,0];
        const min = _.minBy(accounts, a => a.created).created;
        const max = _.maxBy(accounts, a => a.created).created;
        return [
            dayjs(min).startOf('month').valueOf(),
            dayjs(new Date().getTime()).endOf('day').valueOf()
        ];
    }, [ accounts ]);

    const [ minLastActivity, maxLastActivity ] = useMemo(() => {
        if (!accounts) return [0,0];
        const min = _(accounts).map(a => _(a.updates).maxBy(o => o.created)).compact().minBy(o => o.created).created;
        const max = _(accounts).map(a => _(a.updates).maxBy(o => o.created)).compact().maxBy(o => o.created).created;
        return [ 
            dayjs(min).startOf('month').valueOf(),
            dayjs(new Date().getTime()).endOf('day').valueOf()
        ];
    }, [ accounts ]);

    const _dateCreated = useMemo(() => {
        if (!_.isEmpty(dateCreated)) return dateCreated;
        return [ minCreated, maxCreated ];
    }, [ minCreated, maxCreated, dateCreated ]);

    const _lastActivity = useMemo(() => {
        if (!_.isEmpty(lastActivity)) return lastActivity;
        return [ minLastActivity, maxLastActivity ];
    }, [ minLastActivity, maxLastActivity, lastActivity ]);

    function onDeleteSource({ s }) {
        setSources(_.without(sources, s));
    }

    function onDeleteDestination({ s }) {
        setDestinations(_.without(destinations, s));
    }

    return (
        <Box sx={{ display: 'flex' }}>
            <BasicPopover 
                title={
                    _dateCreated[0] === minCreated && _dateCreated[1] === maxCreated ? 'Date Created' :
                    _dateCreated[0] !== minCreated && _dateCreated[1] === maxCreated ? `Later than ${formatDisplayDate(_dateCreated[0])}` :
                    _dateCreated[0] === minCreated && _dateCreated[1] !== maxCreated ? `Earlier than ${formatDisplayDate(_dateCreated[1])}` :
                    `Between ${formatDisplayDate(_dateCreated[0])} and ${formatDisplayDate(_dateCreated[1])}`}
                sx={{ marginRight: '10px' }}
                buttonVariant={_dateCreated[0] !== minCreated || _dateCreated[1] !== maxCreated ? 'contained' : 'outlined'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ width: '400px' }}>
                        <Slider
                            getAriaLabel={() => 'Date Created'}
                            value={_dateCreated}
                            onChange={(e, v) => setDateCreated(v)}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => formatDisplayDate(value)}
                            min={minCreated}
                            max={maxCreated}
                            step={DAY}
                        />
                    </Box>
                </Paper>
            </BasicPopover>
            <BasicPopover 
                title={
                    _lastActivity[0] === minLastActivity && _lastActivity[1] === maxLastActivity ? 'Last Activity' :
                    _lastActivity[0] !== minLastActivity && _lastActivity[1] === maxLastActivity ? `Later than ${formatDisplayDate(_lastActivity[0])}` :
                    _lastActivity[0] === minLastActivity && _lastActivity[1] !== maxLastActivity ? `Earlier than ${formatDisplayDate(_lastActivity[1])}` :
                    `Between ${formatDisplayDate(_lastActivity[0])} and ${formatDisplayDate(_lastActivity[1])}`}
                sx={{ marginRight: '10px' }}
                buttonVariant={_lastActivity[0] !== minLastActivity || _lastActivity[1] !== maxLastActivity ? 'contained' : 'outlined'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ width: '400px' }}>
                        <Slider
                            getAriaLabel={() => 'Last Activity'}
                            value={_lastActivity}
                            onChange={(e, v) => {
                                if (v[0] === minLastActivity && v[1] === maxLastActivity) {
                                    setLastActivity([]);
                                } else {
                                    setLastActivity(v)
                                }
                            }}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => formatDisplayDate(value)}
                            min={minLastActivity}
                            max={maxLastActivity}
                            step={DAY}
                        />
                        <Box>
                            <Button sx={{ fontSize: '11px !important', minWidth: '0px', padding: '0px', textTransform: 'none'}} onClick={() => setLastActivity([minLastActivity, maxLastActivity])}>All</Button>
                            <br/>
                            {[7, 30, 90].map(days => (
                                <span key={days}>
                                    <Button sx={{ fontSize: '11px !important', minWidth: '0px', padding: '0px', textTransform: 'none'}} onClick={() => setLastActivity([maxLastActivity - days * 24 * 60 * 60 * 1000, maxLastActivity])}>
                                        Updated in the {days} days
                                    </Button>
                                    <br/>
                                    <Button sx={{ fontSize: '11px !important', minWidth: '0px', padding: '0px', textTransform: 'none'}} onClick={() => setLastActivity([minLastActivity, maxLastActivity - days * 24 * 60 * 60 * 1000])}>
                                        Hasn&apos;t been updated in {days} days
                                    </Button>
                                    <br/>
                                </span>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </BasicPopover>
            <BasicPopover 
                title={(sources.length + destinations.length) === 0 ? 'Tradelines' : `Picked ${sources.length} origin${sources.length.length !== 1 ? 's' : ''} and ${destinations.length} destination${destinations.length.length !== 1 ? 's' : ''}`}
                sx={{ marginRight: '10px' }}
                buttonVariant={(sources.length + destinations.length) === 0 ? 'outlined' : 'contained'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', width: '400px' }}>
                        <Where 
                            transportationMethod={'sea'}
                            errors={errors}
                            setSource={(v) => { setSources([...sources, v]) }}
                            setDestination={(v) => { setDestinations([...destinations, v]) }}
                            seaPorts={seaPorts}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', width: '400px' }}>
                        <Box sx={{ flex: 1, padding: '3px', minWidth: '235px', maxWidth: '235px' }}>
                            {sources.map(s => ( 
                                <Chip 
                                    key={s.text} 
                                    label={s.text} 
                                    sx={{ margin: '3px' }} 
                                    onDelete={() => onDeleteSource({s})} 
                                />
                            ))}
                        </Box>
                        <Box sx={{ flex: 1, padding: '3px', minWidth: '235px', maxWidth: '235px' }}>
                            {destinations.map(s => ( 
                                <Chip 
                                    key={s.text} 
                                    label={s.text}
                                    sx={{ margin: '3px' }} 
                                    onDelete={() => onDeleteDestination({s})}
                                />
                            ))}
                        </Box>
                    </Box>
                    <FormControlLabel control={<Switch size='small' checked={incEverywhere} onChange={() => setIncEverywhere(!incEverywhere)}/>} label={<Typography variant='caption'>Include "Everywhere".</Typography>} />
                </Paper>
            </BasicPopover>
            <BasicPopover 
                title={
                    !minPP && maxPP === 1000000 ? 'Potential Profit' :
                    minPP && maxPP === 1000000 ? `More than ${minPP}` :
                    !minPP && maxPP < 1000000 ? `Less than ${maxPP}` :
                    `Between ${minPP} and ${maxPP}`}
                sx={{ marginRight: '10px' }}
                buttonVariant={!!minPP || maxPP < 1000000 ? 'contained' : 'outlined'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ width: '400px' }}>
                        <Slider
                            getAriaLabel={() => 'Potential Profit Range'}
                            value={potentialProfit}
                            onChange={(e, v) => setPotentialProfit(v)}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => Number(value).toLocaleString()}
                            min={0}
                            max={1000000}
                            step={100}
                        />
                    </Box>
                </Paper>
            </BasicPopover>
            <BasicPopover 
                title={
                    !minAP && maxAP === 1000000 ? 'Actual Profit' :
                    minAP && maxAP === 1000000 ? `More than ${minAP}` :
                    !minAP && maxAP < 1000000 ? `Less than ${maxAP}` :
                    `Between ${minAP} and ${maxAP}`}
                sx={{ marginRight: '10px' }}
                buttonVariant={!!minAP || maxAP < 1000000 ? 'contained' : 'outlined'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ width: '400px' }}>
                        <Slider
                            getAriaLabel={() => 'PActual Profit Range'}
                            value={actualProfit}
                            onChange={(e, v) => setActualProfit(v)}
                            valueLabelDisplay='auto'
                            getAriaValueText={''}
                            valueLabelFormat={(value) => Number(value).toLocaleString()}
                            min={0}
                            max={1000000}
                            step={100}
                        />
                    </Box>
                </Paper>
            </BasicPopover>
            <BasicPopover 
                title={'Saved Audience'}
                sx={{ marginRight: '10px' }}
                buttonVariant={'outlined'}
             >
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ width: '400px' }}>
                    </Box>
                </Paper>
            </BasicPopover>
        </Box>
    );
}

function BasicPopover(props) {
    const { title, sx, buttonVariant } = props;
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' + Math.random() : undefined;

    return (
        <Box sx={sx}>
            <Button aria-describedby={id} variant={buttonVariant} onClick={handleClick}>
                {title}
                {open ? <FontAwesomeIcon icon={faTimes} style={{ marginLeft: '6px' }} /> : <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '6px' }} />}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{ paper: { sx: { overflow: 'visible' }}}}
            >
                {props.children}
            </Popover>
        </Box>
    );
}

const DAY = 1000 * 60 * 60 * 24;

export default Filters;
